/** Interface and content languages, in the order shown in the language menu. */
export const LOCALE_IDS = ['en', 'uk', 'pl', 'ro', 'es', 'ru'] as const;
export type LocaleId = (typeof LOCALE_IDS)[number];

export const LOCALE_NAMES: Record<LocaleId, string> = {
  en: 'English',
  uk: 'Українська',
  pl: 'Polski',
  ro: 'Română',
  es: 'Español',
  ru: 'Русский',
};

/** BCP 47 tags for dates and numbers. */
export const LOCALE_TAGS: Record<LocaleId, string> = {
  en: 'en-US',
  uk: 'uk-UA',
  pl: 'pl-PL',
  ro: 'ro-RO',
  es: 'es-ES',
  ru: 'ru-RU',
};

export function isLocaleId(value: unknown): value is LocaleId {
  return typeof value === 'string' && (LOCALE_IDS as readonly string[]).includes(value);
}

/** A stored or requested language, falling back to English. */
export function toLocale(value: string | null | undefined): LocaleId {
  return isLocaleId(value) ? value : 'en';
}

/** Best match for a browser language such as "pl-PL" or "ro-MD". */
export function localeFromLanguage(lang: string | null | undefined): LocaleId | null {
  const base = (lang ?? '').toLowerCase().split(/[-_]/)[0];
  if (base === 'mo') return 'ro';
  return isLocaleId(base) ? base : null;
}

/**
 * Public (marketing) pages live under a language prefix: English at the root ("/pricing"),
 * other languages under "/uk/pricing". The home page of a language ends with a slash ("/uk/").
 */
export function localizedPath(locale: LocaleId, path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (locale === 'en') return clean;
  return clean === '/' ? `/${locale}/` : `/${locale}${clean}`;
}

/** Splits "/uk/pricing" into the language and the unprefixed path; English has no prefix. */
export function splitLocalePath(pathname: string): {
  locale: LocaleId;
  path: string;
  prefixed: boolean;
} {
  const match = /^\/([a-z]{2})(?=\/|$)(.*)$/.exec(pathname);
  if (match && match[1] !== 'en' && isLocaleId(match[1])) {
    return { locale: match[1], path: match[2] || '/', prefixed: true };
  }
  return { locale: 'en', path: pathname || '/', prefixed: false };
}
